import { profileData } from "apis/profileData";
import classNames from "classnames";
import { SUBSCRIPTION_CANCELLATION_MODAL_OPENED } from "constants/analyticsEvents";
import { YEARLY } from "constants/billingCycles";
import { MM_SPACE_YYYY } from "constants/date";
import { MONTHLY } from "constants/stripeCouponPeriods";
import * as STRIPE_COUPON_TYPES from "constants/stripeCouponTypes";
import dayjs from "dayjs";
import { BILLING_CYCLE } from "dictionaries";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import SmallSparkle from "static/images/SmallSparkle.svg";
import { useGetTeamMembersQuery } from "store/apis/teamSetting";
import {
  setOpenSeatManageModal,
  setSubscriptionSummaryModalOpen,
} from "store/slices/subscriptionSummary";
import { RootState } from "store/store";
import { SubscriptionInProfile } from "types/Models/subscription";
import { BackendError } from "types/utils/error";
import { capitalize } from "utils/capitalize";
import { formatDate } from "utils/formatDate";
import { AmountSuperScript } from "./design/AmountSuperScript";
import { Button } from "./DesignSystem/Button/Button";
import Modal from "./DesignSystem/Modal/Modal";
import { PencilWithLine } from "./icons/PencilWithLine";
import { RocketPurpleAnimate } from "./icons/RocketPurpleAnimate";
import { PlatformSubscriptionCancelModal } from "./PlatformSubscriptionCancelModal";
import { SubscriptionModal } from "./SubscriptionModal/SubscriptionModal";
import {
  BenefitPoint,
  SubscriptionInfo,
  VerticalRow,
} from "./SubscriptionOverviewComponents";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";

export const SubscriptionUpdateModal = () => {
  const { isOpen } = useSelector(
    (state: RootState) => state.subscriptionSummary
  );
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const cancelationModal = useModal();
  const { groupId } = useParams<{ groupId?: string }>();
  const { authtoken, updateUser } = useContext(authContext);
  const {
    platform_subscription: groupPlatformSubscription,
    uuid: groupIdFromContext,
    entities,
  } = useCurrentGroupContext();
  const groupUuid = groupId || groupIdFromContext;
  const entityId = entities[0]?.uuid;

  const { data: subscriptions } = useGetSubscriptionsQuery(
    {
      groupId: groupUuid,
      entityId,
    },
    {
      skip: !groupUuid || !entityId,
    }
  );
  let {
    platform_subscription: currentSubscription,
  }: {
    platform_subscription: SubscriptionInProfile;
  } = authtoken || {};

  if (!Boolean(currentSubscription.subscription_name)) {
    currentSubscription = groupPlatformSubscription;
  }
  const selectedMetadata = subscriptions?.find(
    (sub) => sub.uuid === currentSubscription?.subscription_uuid
  )?.metadata;

  const { credits } = useSelector((state: RootState) => state.credits);
  const { alertToast } = useToast();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    data = {
      active_group_users_count: 0,
      available_seats: 0,
      extra_seat_amount: 0,
      free_seats: 0,
    },
  } = useGetTeamMembersQuery(
    { groupId: groupId || groupIdFromContext },
    { skip: !groupId && !groupIdFromContext }
  );

  const { isCpa, isGroupAdmin } = useRoleBasedView();

  const closeSubscriptionModal = () => {
    setSubscriptionModal(false);
  };
  const { trackEvent } = useAnalytics();

  const handleClose = () => {
    dispatch(setSubscriptionSummaryModalOpen(false));
  };

  const isCurrentYearIncorporated =
    dayjs(currentSubscription.scheduled_date)
      .subtract(1, "year")
      .year()
      .toString() === new Date().getFullYear().toString();

  const onCancelSubscription = () => {
    trackEvent(SUBSCRIPTION_CANCELLATION_MODAL_OPENED, {
      subscription_id: currentSubscription.uuid,
    });
    cancelationModal.open();
    handleClose();
  };

  const isAnnualSubscription = currentSubscription.billing_cycle === YEARLY;
  const subscriptionAmount =
    currentSubscription.stripe_coupon?.discounted_amount ||
    currentSubscription.amount;

  const openSeatManage = () => {
    dispatch(setOpenSeatManageModal(true));
  };

  const handleCancelSubscription = async () => {
    try {
      cancelationModal.close();
      const profile = await profileData();
      updateUser(profile);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleContactUs = () => {
    handleClose();
    history.push("/chat");
  };

  const extraSeats =
    data?.active_group_users_count + data?.available_seats - data?.free_seats;

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={handleClose}>
        <Modal.Content size="regular">
          <Modal.Header className="t-bg-purple-0 t-flex t-justify-between t-rounded-tl-lg t-rounded-tr-lg !t-pl-6">
            <div className="t-flex t-items-center t-gap-4">
              <RocketPurpleAnimate />
              <div>
                <div className="t-text-subtext t-text-text-100">
                  {currentSubscription.subscription_name}
                </div>
                <div className="t-flex t-gap-1.5 t-items-center">
                  <div className="t-flex t-gap-2 t-items-center">
                    <span
                      className={classNames(
                        "t-text-subtext-sm t-text-neutral-80",
                        {
                          "t-line-through": isCurrentYearIncorporated,
                        }
                      )}
                    >
                      <AmountSuperScript
                        amount={Number(
                          currentSubscription.stripe_coupon
                            ?.discounted_amount || subscriptionAmount
                        )}
                      />
                      <span>
                        /
                        {BILLING_CYCLE[isAnnualSubscription ? YEARLY : MONTHLY]}
                      </span>
                    </span>
                    {isCurrentYearIncorporated && (
                      <div className="t-text-subtext">Free</div>
                    )}
                  </div>
                  {currentSubscription.stripe_coupon && (
                    <span className="t-text-body-sm t-flex t-gap-0.5 t-text-dark_green-70 t-px-1.5 t-py-0.5 t-bg-dark_green-10 t-rounded-2xl">
                      {currentSubscription.stripe_coupon.discount_type ===
                      STRIPE_COUPON_TYPES.PERCENT
                        ? `${currentSubscription.stripe_coupon.discount_rate}%`
                        : `$${currentSubscription.stripe_coupon.discount_rate}`}{" "}
                      Off with {currentSubscription.stripe_coupon?.coupon_code}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="!t-px-6">
            <div className="t-grid t-grid-cols-5 t-items-center t-gap-y-6 t-mb-6 t-overflow-hidden">
              <SubscriptionInfo
                label="Started from"
                value={
                  currentSubscription.started_on
                    ? dayjs(currentSubscription.started_on).format(
                        MM_SPACE_YYYY
                      )
                    : "-"
                }
              />
              <VerticalRow />
              <SubscriptionInfo
                label="Frequency"
                value={capitalize(isAnnualSubscription ? YEARLY : MONTHLY)}
              />
              <VerticalRow />
              <SubscriptionInfo
                label="Next Billing Date"
                value={
                  currentSubscription?.scheduled_date
                    ? formatDate(currentSubscription?.scheduled_date)
                    : "-"
                }
              />
              <SubscriptionInfo
                label={
                  <div className="t-flex t-items-center t-gap-1">
                    Tier Seats{" "}
                    {isGroupAdmin && (
                      <button
                        className="all:unset t-text-purple"
                        onClick={openSeatManage}
                      >
                        <PencilWithLine />
                      </button>
                    )}
                  </div>
                }
                value={data?.free_seats}
              />
              <VerticalRow />
              <SubscriptionInfo
                label="Active users"
                value={data?.active_group_users_count}
              />
              <VerticalRow />
              <SubscriptionInfo label="Seats added" value={extraSeats} />
            </div>
            <div className="t-border t-border-solid t-border-purple-0 t-p-1 t-rounded-lg">
              <div className="t-flex t-items-center t-gap-2  t-px-3 t-py-1">
                <img src={SmallSparkle} alt="SmallSparkle" />
                <span className="t-text-body-sm t-text-text-60">
                  Plan benefits
                </span>
              </div>
              <div className="t-rounded-lg t-bg-surface-lighter-grey t-space-y-1.5 t-p-3">
                {selectedMetadata?.benefits?.map((feature, index) => (
                  <BenefitPoint key={index} description={feature} />
                ))}
              </div>
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={handleContactUs} customType="ghost">
              Contact us
            </Button>
            <Button onClick={onCancelSubscription}>Cancel subscription</Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>

      {currentSubscription && (
        <PlatformSubscriptionCancelModal
          isOpen={cancelationModal.isOpen}
          onClose={cancelationModal.close}
          AccessPoints={[
            Boolean(credits) && (
              <>
                You will lose <b>{credits} Inkle Credits</b>
              </>
            ),
            "Chat history & any pending or upcoming tasks",
            "Clarified transactions & interco ledger in Inkle Books",
            "Deals on softwares & introductions through Inkle Perks",
            "Access to Deadline alerts & compliance health checks",
          ]}
          planCancelLabel="Are you sure you want to cancel Inkle Tax Standard Plan and
              downgrade to free plan?"
          dataExportable
          handleCancelSubscription={handleCancelSubscription}
          subscription={currentSubscription}
          isCurrentYearIncorporated={isCurrentYearIncorporated}
          groupSubscriptionId={currentSubscription.uuid}
        />
      )}

      <SubscriptionModal
        onPaymentDone={closeSubscriptionModal}
        show={subscriptionModal}
        onClose={closeSubscriptionModal}
      />
    </>
  );
};
